import React, { useState } from 'react';
import { Box, Text, Group, Stack, Paper, Transition } from '@mantine/core';
type PersonaType = 'sales' | 'architect' | 'designer';
interface DataPoint {
  id: number;
  text: string;
  shortText: string;
  persona: PersonaType;
  x: number; // 0-100, where 0 = low effort, 100 = high effort
  y: number; // 0-100, where 0 = low impact, 100 = high impact
  priority: 'high' | 'medium' | 'low';
  context?: string;
}
const dataPoints: DataPoint[] = [
// HIGH IMPACT / LOW EFFORT (Quick Wins - Highest Priority)
{
  id: 1,
  text: 'As a sales person, I want access to training materials for the products in the catalog',
  shortText: 'Training Materials Access',
  persona: 'sales',
  x: 25,
  y: 92,
  priority: 'high',
  context: 'Critical for onboarding and product knowledge. Low technical lift with high user value.'
}, {
  id: 2,
  text: 'As a sales person, I need to be able to place the order',
  shortText: 'Place Order',
  persona: 'sales',
  x: 18,
  y: 88,
  priority: 'high',
  context: 'Core functionality - must be seamless and error-free.'
}, {
  id: 3,
  text: 'As a sales person, I want to add products to a cart without obstacles so that I can create a quote and make a sale',
  shortText: 'Add to Cart',
  persona: 'sales',
  x: 12,
  y: 82,
  priority: 'high',
  context: 'Fundamental e-commerce pattern. Reduces friction in the sales process.'
}, {
  id: 4,
  text: 'As a sales person, I need to create a quote',
  shortText: 'Create Quote',
  persona: 'sales',
  x: 22,
  y: 75,
  priority: 'high',
  context: 'Primary user task. Must support bulk operations.'
}, {
  id: 5,
  text: 'As a sales person, I need the customer to know the order has been placed',
  shortText: 'Order Confirmation',
  persona: 'sales',
  x: 8,
  y: 68,
  priority: 'high',
  context: 'Automated notifications reduce manual follow-up.'
}, {
  id: 6,
  text: 'As a sales person, I need to send an estimate to customers',
  shortText: 'Send Estimate',
  persona: 'sales',
  x: 15,
  y: 65,
  priority: 'high',
  context: 'PDF/email generation with branding.'
}, {
  id: 7,
  text: 'As a sales person, I need to be able to track the progress of an order',
  shortText: 'Track Order Progress',
  persona: 'sales',
  x: 28,
  y: 62,
  priority: 'high',
  context: 'Real-time status updates reduce support tickets.'
}, {
  id: 8,
  text: 'As a sales person, I need to be able to build an order from a quote so that I can finish the sales process',
  shortText: 'Quote to Order',
  persona: 'sales',
  x: 38,
  y: 90,
  priority: 'high',
  context: 'Seamless conversion from quote to order is critical.'
},
// HIGH IMPACT / MEDIUM EFFORT
{
  id: 9,
  text: 'As a sales person, I want to be presented with potential enhancements to my cart',
  shortText: 'Cart Enhancements',
  persona: 'sales',
  x: 42,
  y: 78,
  priority: 'medium',
  context: 'Upsell/cross-sell recommendations powered by ML.'
}, {
  id: 10,
  text: 'As a sales person, I want the cart to intelligently bundle products as I add them',
  shortText: 'Intelligent Bundling',
  persona: 'sales',
  x: 45,
  y: 70,
  priority: 'medium',
  context: 'Automatic bundle detection and pricing optimization.'
}, {
  id: 11,
  text: 'As a solutions designer, I want to create a product so that it can be sold or added to bundles',
  shortText: 'Create Product',
  persona: 'designer',
  x: 55,
  y: 75,
  priority: 'medium',
  context: 'Product creation workflow with validation and approval.'
}, {
  id: 12,
  text: 'As a solutions architect, I want to establish API connections between BuSS and a vendor',
  shortText: 'API Connections',
  persona: 'architect',
  x: 62,
  y: 78,
  priority: 'medium',
  context: 'Technical integration requiring security review.'
}, {
  id: 13,
  text: 'As a sales person, I need to create a quote',
  shortText: 'Create Quote (Bulk)',
  persona: 'sales',
  x: 52,
  y: 68,
  priority: 'medium',
  context: 'Bulk quote creation for enterprise deals.'
},
// HIGH IMPACT / HIGH EFFORT (Major Projects)
{
  id: 14,
  text: 'As a solutions designer, I want to create a bundle so that sales are efficient and structured',
  shortText: 'Create Bundle',
  persona: 'designer',
  x: 92,
  y: 95,
  priority: 'medium',
  context: 'Complex bundle configuration with pricing rules.'
}, {
  id: 15,
  text: "As a sales person, I want to know about bundles based on my customer's needs so that I can build the best possible quote",
  shortText: 'Bundle Recommendations',
  persona: 'sales',
  x: 78,
  y: 92,
  priority: 'medium',
  context: 'AI-powered recommendations based on customer profile.'
}, {
  id: 16,
  text: "As a sales person, I want to see the discount rules being applied to my cart's products and bundles",
  shortText: 'Discount Visibility',
  persona: 'sales',
  x: 85,
  y: 85,
  priority: 'medium',
  context: 'Transparent pricing with rule explanations.'
}, {
  id: 17,
  text: 'As a sales person, I want to move products and services from one account line to another',
  shortText: 'Move Products',
  persona: 'sales',
  x: 72,
  y: 88,
  priority: 'medium',
  context: 'Drag-and-drop reorganization of quotes.'
}, {
  id: 18,
  text: 'As a solutions architect, I want to onboard a vendor so that their products can be added to the catalog',
  shortText: 'Vendor Onboarding',
  persona: 'architect',
  x: 68,
  y: 82,
  priority: 'medium',
  context: '6-12 month process currently - needs automation.'
}, {
  id: 19,
  text: 'As a sales person, I want to freely browse the complete catalog of products and services so that I can learn or build carts',
  shortText: 'Browse Catalog',
  persona: 'sales',
  x: 82,
  y: 80,
  priority: 'medium',
  context: 'Searchable, filterable product catalog.'
}, {
  id: 20,
  text: 'As a sales person, I want to clone an existing/older quote to use as a template for a new quote',
  shortText: 'Clone Quote',
  persona: 'sales',
  x: 75,
  y: 72,
  priority: 'medium',
  context: 'Template system for recurring customers.'
}, {
  id: 21,
  text: 'As a sales person, I want to manage customer account to be able to merge, split, or consolidate',
  shortText: 'Account Management',
  persona: 'sales',
  x: 95,
  y: 65,
  priority: 'low',
  context: 'Complex account hierarchy management.'
},
// MEDIUM IMPACT / LOW EFFORT
{
  id: 22,
  text: "As a sales person, I want access to commissions and incentives data so that I know what I'm incentivized to sell",
  shortText: 'Commission Data',
  persona: 'sales',
  x: 12,
  y: 48,
  priority: 'medium',
  context: 'Integration with compensation systems.'
}, {
  id: 23,
  text: 'As a sales person, I want access to sales analytics for products and bundles in a dashboard or product view',
  shortText: 'Sales Analytics',
  persona: 'sales',
  x: 35,
  y: 55,
  priority: 'medium',
  context: 'Performance dashboards and reporting.'
},
// MEDIUM IMPACT / MEDIUM-HIGH EFFORT
{
  id: 24,
  text: "As a solutions designer, I want to import multiple products at one time so that it doesn't take as long",
  shortText: 'Bulk Import',
  persona: 'designer',
  x: 65,
  y: 52,
  priority: 'medium',
  context: 'CSV/Excel import with validation.'
}, {
  id: 25,
  text: 'As a sales person, I need to create a customer account on the fly so that I can build carts for them',
  shortText: 'Quick Account Creation',
  persona: 'sales',
  x: 58,
  y: 58,
  priority: 'medium',
  context: 'Streamlined customer onboarding.'
},
// LOW IMPACT / LOW EFFORT
{
  id: 26,
  text: 'As a solutions designer, I want to attach files from the vendor to the product record',
  shortText: 'Attach Files',
  persona: 'designer',
  x: 18,
  y: 32,
  priority: 'low',
  context: 'Document management for product specs.'
}, {
  id: 27,
  text: 'As a sales person, I want to view comparisons between products so that I can make my own conclusions about which to sell',
  shortText: 'Product Comparison',
  persona: 'sales',
  x: 28,
  y: 38,
  priority: 'low',
  context: 'Side-by-side feature comparison.'
},
// LOW IMPACT / MEDIUM-HIGH EFFORT
{
  id: 28,
  text: 'As a sales person, I need to know when an order has been completed',
  shortText: 'Order Completion',
  persona: 'sales',
  x: 72,
  y: 35,
  priority: 'low',
  context: 'Notification system integration.'
}, {
  id: 29,
  text: 'As a sales person, I need to know the order will be billed properly',
  shortText: 'Billing Verification',
  persona: 'sales',
  x: 78,
  y: 30,
  priority: 'low',
  context: 'Billing system integration and validation.'
}, {
  id: 30,
  text: 'As a sales person, I want to view customer feedback on products so that I can see possible problems',
  shortText: 'Customer Feedback',
  persona: 'sales',
  x: 55,
  y: 25,
  priority: 'low',
  context: 'Feedback aggregation from multiple sources.'
}, {
  id: 31,
  text: 'As a sales person, I want to know that everything is working as specified',
  shortText: 'System Health',
  persona: 'sales',
  x: 48,
  y: 18,
  priority: 'low',
  context: 'Status dashboard and monitoring.'
}, {
  id: 32,
  text: 'As a solutions designer, I want to set pricing and margin options for bundles so that each channel gets its own pricing rules',
  shortText: 'Channel Pricing',
  persona: 'designer',
  x: 68,
  y: 15,
  priority: 'low',
  context: 'Complex pricing engine with channel rules.'
}, {
  id: 33,
  text: 'As a solutions designer, I want to see my product approved by a solutions administrator',
  shortText: 'Product Approval',
  persona: 'designer',
  x: 25,
  y: 8,
  priority: 'low',
  context: 'Approval workflow with notifications.'
}];
const personaColors: Record<PersonaType, {
  bg: string;
  border: string;
  glow: string;
}> = {
  sales: {
    bg: 'rgba(251, 191, 36, 0.8)',
    border: 'rgba(251, 191, 36, 1)',
    glow: 'rgba(251, 191, 36, 0.4)'
  },
  architect: {
    bg: 'rgba(59, 130, 246, 0.8)',
    border: 'rgba(59, 130, 246, 1)',
    glow: 'rgba(59, 130, 246, 0.4)'
  },
  designer: {
    bg: 'rgba(34, 197, 94, 0.8)',
    border: 'rgba(34, 197, 94, 1)',
    glow: 'rgba(34, 197, 94, 0.4)'
  }
};
const personaLabels: Record<PersonaType, string> = {
  sales: 'Sales Person',
  architect: 'Solutions Architect',
  designer: 'Solutions Designer'
};
export function ImpactEffortMatrix() {
  const [hoveredPoint, setHoveredPoint] = useState<DataPoint | null>(null);
  const [activeFilter, setActiveFilter] = useState<PersonaType | 'all'>('all');
  const filteredPoints = activeFilter === 'all' ? dataPoints : dataPoints.filter(p => p.persona === activeFilter);
  const getNodeSize = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return 48;
      case 'medium':
        return 38;
      case 'low':
        return 28;
    }
  };
  return <Box className="relative" data-id="element-165">
      {/* Legend */}
      <Group justify="space-between" align="flex-start" mb="xl" wrap="wrap" gap="md" data-id="element-166">
        <Stack gap="xs" data-id="element-167">
          <Text size="sm" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.1em'
        }} data-id="element-168">
            Persona Types
          </Text>
          <Group gap="lg" data-id="element-169">
            {(Object.keys(personaColors) as PersonaType[]).map(persona => <button key={persona} onClick={() => setActiveFilter(activeFilter === persona ? 'all' : persona)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-300 ${activeFilter === persona || activeFilter === 'all' ? 'opacity-100' : 'opacity-40'}`} style={{
            borderColor: personaColors[persona].border,
            backgroundColor: activeFilter === persona ? personaColors[persona].bg : 'transparent'
          }} data-id="element-170">
                <span className="w-3 h-3 rounded-full" style={{
              backgroundColor: personaColors[persona].bg
            }} data-id="element-171" />
                <span className={`text-sm ${activeFilter === persona ? 'text-black font-medium' : 'text-gray-400'}`} data-id="element-172">
                  {personaLabels[persona]}
                </span>
              </button>)}
          </Group>
        </Stack>

        <Stack gap="xs" data-id="element-173">
          <Text size="sm" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.1em'
        }} data-id="element-174">
            Priority (Node Size)
          </Text>
          <Group gap="md" data-id="element-175">
            {(['high', 'medium', 'low'] as const).map(priority => <Group key={priority} gap="xs" align="center" data-id="element-176">
                <span className="rounded-full bg-white/20 border border-white/30" style={{
              width: getNodeSize(priority) / 2,
              height: getNodeSize(priority) / 2
            }} data-id="element-177" />
                <Text size="xs" c="dimmed" tt="capitalize" data-id="element-178">
                  {priority}
                </Text>
              </Group>)}
          </Group>
        </Stack>
      </Group>

      {/* Matrix Container */}
      <Box className="relative rounded-2xl bg-zinc-900" style={{
      border: '1px solid rgba(255, 255, 255, 0.1)',
      aspectRatio: '16/10'
    }} data-id="element-179">
        {/* Grid Lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
        opacity: 0.15
      }} data-id="element-180">
          {/* Vertical grid lines */}
          {[...Array(11)].map((_, i) => <line key={`v-${i}`} x1={`${i * 10}%`} y1="0" x2={`${i * 10}%`} y2="100%" stroke="white" strokeWidth="1" strokeDasharray={i === 5 ? '0' : '4 4'} data-id="element-181" />)}
          {/* Horizontal grid lines */}
          {[...Array(11)].map((_, i) => <line key={`h-${i}`} x1="0" y1={`${i * 10}%`} x2="100%" y2={`${i * 10}%`} stroke="white" strokeWidth="1" strokeDasharray={i === 5 ? '0' : '4 4'} data-id="element-182" />)}
        </svg>

        {/* Axis Labels */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center bg-zinc-900 gap-2" data-id="element-183">
          <div className="w-2 h-2 rounded-full bg-white/40" data-id="element-184" />
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.2em'
        }} data-id="element-185">
            High Impact
          </Text>
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center bg-zinc-900 gap-2" data-id="element-186">
          <div className="w-2 h-2 rounded-full bg-white/40" data-id="element-187" />
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.2em'
        }} data-id="element-188">
            Low Impact
          </Text>
        </div>
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center bg-zinc-900 gap-2" data-id="element-189">
          <div className="w-2 h-2 rounded-full bg-white/40" data-id="element-190" />
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.2em'
        }} data-id="element-191">
            Low Effort
          </Text>
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center bg-zinc-900 gap-2" data-id="element-192">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{
          letterSpacing: '0.2em'
        }} data-id="element-193">
            High Effort
          </Text>
          <div className="w-2 h-2 rounded-full bg-white/40" data-id="element-194" />
        </div>

        {/* Quadrant Labels */}
        <div className="absolute top-12 left-12 opacity-30" data-id="element-195">
          <Text size="xs" c="white" fw={700} tt="uppercase" style={{
          letterSpacing: '0.15em'
        }} data-id="element-196">
            Quick Wins
          </Text>
        </div>
        <div className="absolute top-12 right-12 opacity-30" data-id="element-197">
          <Text size="xs" c="white" fw={700} tt="uppercase" style={{
          letterSpacing: '0.15em'
        }} data-id="element-198">
            Major Projects
          </Text>
        </div>
        <div className="absolute bottom-12 left-12 opacity-30" data-id="element-199">
          <Text size="xs" c="white" fw={700} tt="uppercase" style={{
          letterSpacing: '0.15em'
        }} data-id="element-200">
            Fill-Ins
          </Text>
        </div>
        <div className="absolute bottom-12 right-12 opacity-30" data-id="element-201">
          <Text size="xs" c="white" fw={700} tt="uppercase" style={{
          letterSpacing: '0.15em'
        }} data-id="element-202">
            Thankless Tasks
          </Text>
        </div>

        {/* Data Points */}
        <div className="absolute inset-0 p-8" data-id="element-203">
          {filteredPoints.map(point => {
          const size = getNodeSize(point.priority);
          const colors = personaColors[point.persona];
          const isHovered = hoveredPoint?.id === point.id;
          return <div key={point.id} className="absolute cursor-pointer transition-all duration-300 ease-out" style={{
            left: `${point.x}%`,
            top: `${100 - point.y}%`,
            transform: `translate(-50%, -50%) scale(${isHovered ? 1.2 : 1})`,
            zIndex: isHovered ? 50 : 10
          }} onMouseEnter={() => setHoveredPoint(point)} onMouseLeave={() => setHoveredPoint(null)} data-id="element-204">
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-full blur-md transition-opacity duration-300" style={{
              backgroundColor: colors.glow,
              opacity: isHovered ? 1 : 0,
              transform: 'scale(1.5)'
            }} data-id="element-205" />

                {/* Node */}
                <div className="relative rounded-full flex items-center justify-center transition-all duration-300" style={{
              width: size,
              height: size,
              backgroundColor: colors.bg,
              border: `2px solid ${colors.border}`,
              boxShadow: isHovered ? `0 0 20px ${colors.glow}, 0 4px 12px rgba(0,0,0,0.3)` : '0 2px 8px rgba(0,0,0,0.2)'
            }} data-id="element-206">
                  {/* Inner highlight */}
                  <div className="absolute inset-1 rounded-full opacity-30" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)'
              }} data-id="element-207" />
                </div>
              </div>;
        })}
        </div>

        {/* Tooltip */}
        <Transition mounted={hoveredPoint !== null} transition="fade" duration={200} data-id="element-208">
          {styles => hoveredPoint && <Paper shadow="xl" p="md" radius="lg" style={{
          ...styles,
          position: 'absolute',
          left: `${Math.min(Math.max(hoveredPoint.x, 20), 80)}%`,
          top: `${Math.min(Math.max(100 - hoveredPoint.y + 8, 10), 70)}%`,
          transform: 'translateX(-50%)',
          zIndex: 100,
          maxWidth: 320,
          backgroundColor: 'rgba(24, 24, 27, 0.8)',
          border: `1px solid rgba(255, 255, 255, .3)`,
          backdropFilter: 'blur(12px)'
        }} data-id="element-209">
                {/* Persona badge */}
                <Group gap="xs" mb="xs" data-id="element-210">
                  <span className="w-2.5 h-2.5 rounded-full" style={{
              backgroundColor: personaColors[hoveredPoint.persona].bg
            }} data-id="element-211" />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{
              letterSpacing: '0.05em'
            }} data-id="element-212">
                    {personaLabels[hoveredPoint.persona]}
                  </Text>
                </Group>

                {/* Title */}
                <Text size="sm" fw={700} c="white" mb="xs" data-id="element-213">
                  {hoveredPoint.shortText}
                </Text>

                {/* Full user story */}
                <Text size="xs" c="dimmed" mb="sm" style={{
            lineHeight: 1.5
          }} data-id="element-214">
                  "{hoveredPoint.text}"
                </Text>

                {/* Context */}
                {hoveredPoint.context && <Box p="xs" style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 8
          }} data-id="element-215">
                    <Text size="xs" c="gray.4" style={{
              lineHeight: 1.5
            }} data-id="element-216">
                      💡 {hoveredPoint.context}
                    </Text>
                  </Box>}

                {/* Priority indicator */}
                <Group gap="xs" mt="sm" data-id="element-217">
                  <Text size="xs" c="dimmed" data-id="element-218">
                    Priority:
                  </Text>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${hoveredPoint.priority === 'high' ? 'bg-green-500/20 text-green-400' : hoveredPoint.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`} data-id="element-219">
                    {hoveredPoint.priority.toUpperCase()}
                  </span>
                </Group>
              </Paper>}
        </Transition>
      </Box>

      {/* Instructions */}
      <Text size="xs" c="dimmed" ta="center" mt="md" style={{
      opacity: 0.6
    }} data-id="element-220">
        Hover over nodes to see details • Click persona labels to filter • Node
        size indicates priority
      </Text>
    </Box>;
}